import { useState } from 'react'
import {
  ROLES,
  ROLE_LABELS,
  useAdminRoles,
  useAdminRolesLookup,
  useAdminRoleAssign,
  useAdminRoleRevoke,
} from '../processes/admin_roles'

const ALL_ROLES = Object.values(ROLES)

export default function AdminRoles() {
  const { isSuperAdmin, isLoading: rolesLoading } = useAdminRoles()
  const { data, isLoading, error } = useAdminRolesLookup()
  const assignMutation = useAdminRoleAssign()
  const revokeMutation = useAdminRoleRevoke()

  const [userId, setUserId] = useState('')
  const [role, setRole] = useState(ROLES.USER_ADMIN)
  const [feedback, setFeedback] = useState(null)

  if (rolesLoading) {
    return null
  }

  if (!isSuperAdmin) {
    return (
      <div className="tw-p-8 tw-text-center tw-text-lg">
        Only Super Admins can manage admin roles.
      </div>
    )
  }

  const handleAssign = async (e) => {
    e.preventDefault()
    setFeedback(null)
    const numericUserId = Number(userId)
    if (!Number.isInteger(numericUserId) || numericUserId <= 0) {
      setFeedback({ type: 'error', message: 'user id must be a positive integer' })
      return
    }
    try {
      await assignMutation.mutateAsync({ userId: numericUserId, role })
      setFeedback({ type: 'success', message: `granted ${role} to user ${numericUserId}` })
      setUserId('')
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err?.response?.data?.message ?? err?.message ?? 'failed to grant role',
      })
    }
  }

  const handleRevoke = async (targetUserId, targetRole) => {
    setFeedback(null)
    try {
      await revokeMutation.mutateAsync({ userId: targetUserId, role: targetRole })
      setFeedback({ type: 'success', message: `revoked ${targetRole} from user ${targetUserId}` })
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err?.response?.data?.message ?? err?.message ?? 'failed to revoke role',
      })
    }
  }

  const rows = Array.isArray(data) ? data : []

  return (
    <div className="tw-p-6 tw-space-y-6">
      <div className="tw-bg-white tw-p-5 tw-rounded tw-shadow">
        <h2 className="tw-text-xl tw-font-bold tw-mb-3">Grant a role</h2>
        <form
          onSubmit={handleAssign}
          className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-items-stretch sm:tw-items-end"
        >
          <label className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-font-medium tw-mb-1">User ID</span>
            <input
              type="number"
              min="1"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              className="tw-input tw-input-bordered tw-w-48"
              placeholder="e.g. 42"
            />
          </label>
          <label className="tw-flex tw-flex-col">
            <span className="tw-text-sm tw-font-medium tw-mb-1">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="tw-select tw-select-bordered tw-w-56"
            >
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r] ?? r}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={assignMutation.isPending}
            className="tw-btn tw-btn-primary"
          >
            {assignMutation.isPending ? 'Granting…' : 'Grant'}
          </button>
        </form>
        {feedback && (
          <div
            className={[
              'tw-mt-3',
              'tw-text-sm',
              feedback.type === 'error' ? 'tw-text-red-600' : 'tw-text-green-700',
            ].join(' ')}
          >
            {feedback.message}
          </div>
        )}
      </div>

      <div className="tw-bg-white tw-p-5 tw-rounded tw-shadow">
        <h2 className="tw-text-xl tw-font-bold tw-mb-3">Current admins</h2>
        {isLoading && <div>Loading…</div>}
        {error && (
          <div className="tw-text-red-600 tw-text-sm">
            Failed to load: {error?.response?.data?.message ?? error?.message}
          </div>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <div className="tw-text-sm tw-text-gray-500">No admin users yet.</div>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="tw-overflow-x-auto">
            <table className="tw-table tw-w-full">
              <thead>
                <tr>
                  <th className="tw-text-left">User ID</th>
                  <th className="tw-text-left">Email</th>
                  <th className="tw-text-left">Name</th>
                  <th className="tw-text-left">Roles</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const userRoles = Array.isArray(u.roles) ? u.roles : []
                  return (
                    <tr key={u.userId}>
                      <td>{u.userId}</td>
                      <td>{u.email}</td>
                      <td>{u.name}</td>
                      <td>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                          {userRoles.map((r) => (
                            <span
                              key={r}
                              className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-px-2 tw-py-1 tw-rounded tw-text-xs"
                            >
                              {ROLE_LABELS[r] ?? r}
                              <button
                                type="button"
                                onClick={() => handleRevoke(u.userId, r)}
                                disabled={revokeMutation.isPending}
                                className="tw-text-red-600 hover:tw-text-red-800"
                                title={`revoke ${r}`}
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
